import React, { useState, useEffect } from "react";
import { Heart, X, ExternalLink } from "lucide-react";
import { ASSETS } from "../lib/assets";

const ZEFFY_SLUG = "literacy-and-leadership-initiative";
const ZEFFY_URL = `https://www.zeffy.com/en-US/donation-form/${ZEFFY_SLUG}`;
const ZEFFY_EMBED_URL = `https://www.zeffy.com/embed/donation-form/${ZEFFY_SLUG}`;

export const Donate = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <section id="donate" data-testid="donate-section" className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={ASSETS.drills} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-[#C4F542] mb-6">/ 05 — Support Our Mission</p>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-8xl text-white leading-none">
            EVERY GIFT<br />
            BUILDS A <span className="text-[#C4F542]">CHAMPION.</span>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-zinc-300 font-body text-base sm:text-lg">
            Your support helps provide life-changing programs, trusted mentors, healthy meals and
            snacks, fitness equipment, and opportunities that empower young people to grow with
            confidence, purpose, and resilience.
          </p>

          <div className="mt-12 bg-[#111111] border border-white/10 p-8 md:p-10 max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-6">Choose Your Gift</p>

            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              {["$25", "$50", "$100", "$250", "Custom"].map((amt) => (
                <span
                  key={amt}
                  className="px-5 py-3 border border-white/15 text-white font-display text-xl min-w-[80px]"
                >
                  {amt}
                </span>
              ))}
            </div>

            <button
              data-testid="donate-open-btn"
              onClick={() => setOpen(true)}
              className="w-full inline-flex items-center justify-center gap-3 bg-[#C4F542] text-black font-bold uppercase tracking-widest text-sm py-5 px-8 hover:bg-white transition-colors duration-300"
            >
              <Heart size={18} />
              Donate Now
            </button>

            <p className="mt-5 text-xs text-zinc-500 font-body">
              Every donation directly supports the mission of Our HOVSE Foundation, a registered
              501(c)(3) nonprofit organization.
            </p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-body">
              Securely processed by Zeffy · 100% fee-free
            </p>
          </div>
        </div>
      </section>

      {open && (
        <div
          data-testid="donate-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/90 backdrop-blur-md"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full max-w-2xl h-[85vh] max-h-[900px] bg-[#111111] border border-[#C4F542]/30 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black">
              <div className="flex items-center gap-2">
                <Heart size={14} className="text-[#C4F542]" />
                <span className="text-xs uppercase tracking-[0.3em] text-white font-bold">
                  Donate to Our HOVSE Foundation
                </span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={ZEFFY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="donate-open-tab"
                  className="h-8 w-8 flex items-center justify-center border border-white/15 text-zinc-300 hover:border-[#C4F542] hover:text-[#C4F542] transition-colors"
                  aria-label="Open donation form in a new tab"
                  title="Open in new tab"
                >
                  <ExternalLink size={14} />
                </a>
                <button
                  data-testid="donate-modal-close"
                  onClick={() => setOpen(false)}
                  className="h-8 w-8 flex items-center justify-center border border-white/15 text-zinc-300 hover:border-[#C4F542] hover:text-[#C4F542] transition-colors"
                  aria-label="Close donation form"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Zeffy iframe */}
            <iframe
              src={ZEFFY_EMBED_URL}
              title="Zeffy donation form"
              data-testid="donate-iframe"
              className="w-full h-[calc(100%-49px)] bg-white"
              allow="payment *; clipboard-read; clipboard-write"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      )}
    </>
  );
};
